import React from 'react';
import { IonIcon } from '@ionic/react';
import { arrowForward, chevronForwardOutline, leaf, sparklesOutline, waterOutline, alertCircle, warning } from 'ionicons/icons';
import CountUp from '../CountUp';
import type { FieldCondition } from '../../../domain/FieldCondition';
import './home.css';

const CONDITION_ICON = {
  ok: leaf,
  caution: warning,
  bad: alertCircle,
} as const;

// La condición del lote/estación en una palabra, colgada del encabezado.
export const ConditionCard: React.FC<{
  label: string;
  condition: FieldCondition;
  onClick?: () => void;
}> = ({ label, condition, onClick }) => (
  <button className={`home-condition level-${condition.level}`} onClick={onClick} disabled={!onClick}>
    <span className="home-condition-icon">
      <IonIcon icon={CONDITION_ICON[condition.level]} />
    </span>
    <span className="home-condition-body">
      <small>{label}</small>
      <strong>{condition.title}</strong>
      <span>{condition.detail}</span>
    </span>
    {onClick && <IonIcon icon={chevronForwardOutline} className="chevron" />}
  </button>
);

// Humedad del suelo con la franja ideal marcada y dónde está ahora.
export const SoilCard: React.FC<{
  title: string;
  value: number;
  lowPct: number;
  saturatedPct: number;
  state: string;
  onClick?: () => void;
}> = ({ title, value, lowPct, saturatedPct, state, onClick }) => {
  const clamp = (n: number) => Math.max(0, Math.min(100, n));
  return (
    <button className="home-soil" onClick={onClick}>
      <span className="home-soil-head">
        <span className="home-soil-icon">
          <IonIcon icon={waterOutline} />
        </span>
        <span className="home-soil-value">
          <small>{title}</small>
          <strong>
            <CountUp value={`${Math.round(value)}%`} />
          </strong>
        </span>
        <span className="home-soil-range">
          Rango ideal
          <br />
          {lowPct}% – {saturatedPct}%
        </span>
      </span>
      <span className="home-soil-bar" aria-label={`${Math.round(value)}%, ${state}`}>
        <span
          className="home-soil-ideal"
          style={{
            left: `${clamp(lowPct)}%`,
            width: `${clamp(saturatedPct) - clamp(lowPct)}%`,
          }}
        />
        <span className="home-soil-fill" style={{ width: `${clamp(value)}%` }} />
        <span className="home-soil-marker" style={{ left: `${clamp(value)}%` }} />
      </span>
    </button>
  );
};

export type MetricTone = 'temp' | 'humidity' | 'wind' | 'rain' | 'pressure' | 'soil';

export interface MetricTileItem {
  key: string;
  tone: MetricTone;
  icon: string;
  value: string;
  unit?: string;
  label: string;
  sub?: string;
  onClick?: () => void;
}

// Las cuatro (o más) tarjetas pastel de "ahora": una por métrica.
// Con 5 o 6 métricas, 3 por fila (todas del mismo tamaño); si no, 4.
export const MetricTiles: React.FC<{ items: MetricTileItem[] }> = ({ items }) => (
  <div className={`home-metrics${items.length === 5 || items.length === 6 ? ' cols-3' : ''}`}>
    {items.map((m) => (
      <button key={m.key} className={`home-metric tone-${m.tone}`} onClick={m.onClick} disabled={!m.onClick}>
        <IonIcon icon={m.icon} />
        {m.onClick && <IonIcon icon={arrowForward} className="home-metric-go" />}
        <strong>
          <CountUp value={m.value} />
          {m.unit && m.value !== '—' && <small> {m.unit}</small>}
        </strong>
        <span>{m.label}</span>
        {m.sub && <small className="home-metric-sub">{m.sub}</small>}
      </button>
    ))}
  </div>
);

// "Domiot recomienda": la lectura más útil del momento; abre el asistente.
export const RecommendCard: React.FC<{
  title: string;
  detail: string;
  onClick: () => void;
}> = ({ title, detail, onClick }) => (
  <button className="home-recommend" onClick={onClick}>
    <span className="home-recommend-icon">
      <IonIcon icon={sparklesOutline} />
    </span>
    <span className="home-recommend-body">
      <small>Domiot recomienda</small>
      <strong>{title}.</strong> {detail}
    </span>
    <IonIcon icon={chevronForwardOutline} className="chevron" />
  </button>
);
